import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PinDialogComponent } from 'src/app/core/components/pin-dialog/pin-dialog.component';
import { WebCamScannerComponent } from 'src/app/core/components/scanner/web-cam-scanner/web-cam-scanner.component';
import { KioskUserService } from 'src/app/shared/services/kiosk-user.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatIconModule } from '@angular/material/icon';
import { BarCodeScannerComponent } from 'src/app/core/components/scanner/bar-code-scanner/bar-code-scanner.component';
import { NavRoutes } from 'src/app/core/constants/nav.routes';
import { Router } from '@angular/router';
import { LoadingSpinnerComponent } from 'src/app/shared/components/loading-spinner/loading-spinner.component';
import { KioskService } from 'src/openapi-gen/services';
import { KioskAffiliatedUserDto, KioskBarcodeDto, KioskRole } from 'src/openapi-gen/models';
import { ToastComponent } from 'src/app/shared/components/toasts/toast.component';
import { MessageService } from 'primeng/api';
import { ErrorResponseHandler } from '../../shared/services/error-response-handler';
import { TransactionTypes } from 'src/app/core/constants/transaction.types';
import { TransactionRoutingService } from 'src/app/shared/services/transaction-routing.service';
import { InfoMessageDialogComponent } from './info-message-dialog/info-message-dialog.component';
import { TransactionPacket } from 'src/app/shared/models/transaction-packet';

@Component({
  selector: 'app-kiosk-scan-page',
  templateUrl: './kiosk-scan-page.component.html',
  styleUrls: ['./kiosk-scan-page.component.scss'],
  standalone: true,
  providers: [MessageService],
  imports: 
  [
    CommonModule,
    FlexModule, 

    MatCardModule,
    MatButtonModule, 
    MatFormFieldModule, 
    MatIconModule, 
    MatDialogModule, 

    BarCodeScannerComponent, 
    LoadingSpinnerComponent, 
    ToastComponent,
    WebCamScannerComponent, 
  ]
})
export class KioskScanPageComponent implements OnInit, OnDestroy {
  @ViewChild(ToastComponent, { static: true }) toast: ToastComponent;
  @ViewChild(LoadingSpinnerComponent, { static: false }) loadingSpinner: LoadingSpinnerComponent;
  @ViewChild("webCam", { static: false }) webCam: WebCamScannerComponent;

  public static hasInfoDialogBeenShown: boolean = false;
  private scanResult?: boolean = null;
  private cameraInterval: number;
  isUserLoggedIn: boolean;
  cameraOn: boolean = true;
  isSignInInProgress: boolean = false;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private kisokService: KioskService,
    private kioskUserService: KioskUserService,
    private transactionRoutingService: TransactionRoutingService,
    private errorResponseHandler:ErrorResponseHandler
  ) { }

  ngOnInit() {
    let affiliatedUser = this.kioskUserService.getAffiliatedUser();
    this.isUserLoggedIn = affiliatedUser !== null;
    
    if(this.isUserLoggedIn) {
      this.routeUser(affiliatedUser);
    } 
    else if(!KioskScanPageComponent.hasInfoDialogBeenShown){
      this.dialog.open(InfoMessageDialogComponent, {
        width: "500px",
        closeOnNavigation: true,
        disableClose: true,
        autoFocus: false
      })
      .afterClosed()
      .subscribe(_ => {
        KioskScanPageComponent.hasInfoDialogBeenShown = true;
      });
    }

    this.startCameraRefresh();
  }

  ngOnDestroy() {
    clearInterval(this.cameraInterval);
  }

  private startCameraRefresh() {
    this.cameraInterval = setInterval(() => {
      if(this.webCam) {
        this.webCam.stopThecamera();
        this.webCam.startTheCamera();
      }
    }, 900_000); // Refresh the camera every 15 minutes
  }

  openPinDialog() {
    this.dialog.open(PinDialogComponent, {
      closeOnNavigation: true,
      disableClose: true,
      data: {
        message: "To continue, please enter your PIN:",
        isLoggingOut: false,
      },
    });
  }

  scannerOn(): void {
    this.cameraOn = true;
    if (this.webCam) {
      this.webCam.startTheCamera();
    }
  }  
  
  scannerOff(): void {
    this.cameraOn = false;
    if (this.webCam) {
      this.webCam.stopThecamera();
    }
  }

  onScanInput(value: string) {
    this.onScan(value);

    this.loadingSpinner.show();
    this.isSignInInProgress = true;

    this.kisokService
      .apiKioskSearchByBarcodePost({ body: { barcode: Number.parseInt(value) } as KioskBarcodeDto })
      .subscribe({
        next: data => {
          this.kioskUserService.saveAffiliatedUser(data);
          this.routeUser(data);
        },
        error: (err) => {
          this.loadingSpinner.hide();
          this.isSignInInProgress = false;
          this.errorResponseHandler.handle_kiosk_barcode_login(err.status, this.toast);
        }
    }); 
  }

  private routeUser(data: KioskAffiliatedUserDto) {
    if(data.role === KioskRole.Agency) {
      this.outGoingForAgency();
    }
    else {
      this.router.navigate([NavRoutes.Kiosk, NavRoutes.KioskOptions]);
    }
  }

  private onScan(value: string) {
    console.debug(`Code scanned on scanner ${value}`);

    if(value) {
      this.scanResult = true;
    } 
    else {
      this.scanResult = false;
    }

    setTimeout(() => this.scanResult = null, 1000)
  }

  getScanResultClass() {
    return this.scanResult ? 'scan-green' : this.scanResult === false ? 'scan-red' : '';
  }

  private outGoingForAgency() {
    let data = {
      title: 'OUTGOING PRODUCT WEIGHT',
      type: TransactionTypes.Outgoing,
      kiosk: true,
    } as TransactionPacket;

    this.transactionRoutingService.updatePacket(data);
    this.router.navigate([NavRoutes.Kiosk, NavRoutes.Transaction]);
  }
}
